import { Table } from "rsuite";
import { useCallback, useMemo, memo, useState } from "react";
import { IconButton } from "../../../../components";
import { FaLink } from "react-icons/fa";
import { MdCallSplit } from "react-icons/md";
import { IoCloseCircle, IoCheckmarkCircle, IoWarning  } from "react-icons/io5";
import { useAuthenticationService } from "../../../../services/authentication.service";
import { useBankFileService } from "../../contexts/BankFileServiceContext";
import { roles } from "../../../../utils/constants";
import ToastService from "../../../../services/toast.service";
import logger from "../../../../utils/logger";
import { Popover, Whisper, Button } from "rsuite";
import CustomModal from "../../../../components/modal";
const { Column, HeaderCell, Cell } = Table;

function ActionsColumn({ rowData, onLinkChild, onSplitTransaction, transactionsService }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleLinkChild = useCallback(() => {
    onLinkChild(rowData);
  }, [onLinkChild, rowData]);

  const handleSplitTransaction = useCallback(() => {
    onSplitTransaction(rowData);
  }, [onSplitTransaction, rowData]);

  const linkButtonClassName = useMemo(() => {
    return `link-child-button ${!rowData?.childId && !rowData?.isSplit && !rowData?.ignore ? "unmatched" : ""}`;
  }, [rowData?.childId, rowData?.isSplit]);

  const handleIgnoreFlag = useCallback(async (flag) => {
    setIsLoading(true);
    try {
      const updatedFlag = { ...flag, ignore: !flag.ignore };
      await transactionsService.updateFlag(updatedFlag);
    } catch (error) {
      logger.error("Error updating flag ignore status:", error);
      ToastService.error("Failed to update flag status. Please try again.");
      
    } finally {
      setIsLoading(false);
      setModalOpen(false);
      setSelectedFlag(null);
    }
  }, [transactionsService]);

  return (
    <>
      <div style={{ display: "flex", gap: "8px" }} className="actions-column">
        <IconButton
          icon={<FaLink />}
          size="xs"
          disabled={rowData?.isSplit || rowData?.exportBatchId}
          height={30}
          width={30}
          onClick={handleLinkChild}
          className={linkButtonClassName}
          data-testid={`link-child-${rowData.id}`}
          title="Link to Child"
        />
        <IconButton
          icon={<MdCallSplit />}
          size="xs"
          height={35}
          width={35}
          disabled={rowData?.exportBatchId}
          appearance="subtle"
          onClick={handleSplitTransaction}
          className="split-transaction-button"
          data-testid={`split-transaction-${rowData.id}`}
          title="Split Transaction"
        />
        {rowData?.flags && rowData.flags.length > 0 && (
          <Whisper placement="auto" speaker={
            <Popover title="Flags" style={{ maxWidth: 300 }}>
              {rowData.flags.map((flag, index) => (
                <div key={flag.id} style={{ marginBottom: index < rowData.flags.length - 1 ? 8 : 0 }}>
                  <div style={{ marginBottom: 4 }}>{flag.message}</div>
                  <Button
                    size="xs"
                    appearance="subtle"
                    className="ignore-flag-button"
                    onClick={() => {
                      setSelectedFlag(flag);
                      setModalOpen(true);
                    }}
                    style={{ padding: '2px 8px', fontSize: '12px' }}
                  >
                    {flag.ignore ? "Unignore" : "Ignore"}
                  </Button>
                </div>
              ))}
            </Popover>
          } trigger="click" key={rowData.flags.map(f => `${f.id}-${f.ignore}`).join('-')}>
            <IconButton
              icon={<IoWarning />}
              size="xs"
              height={30}
              width={30}
              appearance="subtle"
              className={`flags-icon ${rowData.flags.some(f => !f.ignore) ? "active" : ""}`}
              data-testid={`flags-${rowData.id}`}
              title={`${rowData.flags.filter(f => !f.ignore).length} active flag(s)`}
            />
          </Whisper>
        )}
      </div>
      <CustomModal
        title={selectedFlag?.ignore ? "Unignore Flag" : "Ignore Flag"}
        text={`Are you sure you want to ${selectedFlag?.ignore ? "unignore" : "ignore"} this flag?`}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={() => handleIgnoreFlag(selectedFlag)}
        loading={isLoading}
        confirmButtonText={selectedFlag?.ignore ? "Unignore" : "Ignore"}
        cancelButtonText="Cancel"
      />
    </>
  );
}

const MemoizedActionsColumn = memo(ActionsColumn);

function BankTransactionsTable({ bankFile, fileTypes, data, sortColumn, sortType, onSortColumn, onLinkChild, onSplitTransaction, transactionsService }) {
  const authService = useAuthenticationService();
  const bankFileService = useBankFileService();
  const isAdmin = useMemo(() => authService.isInAnyRole(roles.ADMINS), [authService]);

  // Stable amount formatter function
  const formatAmount = useCallback((rowData) => {
    const amt = Number(rowData.amount);
    if (!Number.isFinite(amt)) return rowData.amount ?? "";
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amt);
  }, []);

  const exportAs = useCallback(
    (rowData) => {
      if(!fileTypes || fileTypes.length === 0) return "N/A";
      const id = rowData?.exportTypeId || bankFile?.fileTypeId;
      return fileTypes?.find((ft) => ft.fileTypeId === id)?.code || "N/A";
    },
    [bankFile, fileTypes]
  );

  // Stable export status renderer function
  const renderExportStatus = useCallback(
    (rowData) => {
      return (
        <div className="export-status">
          {rowData?.ignore ? (
            <div>{bankFileService.getIgnoreReasonById(rowData?.ignoreReasonId)?.reason}</div>
          ) : rowData.exportBatchId ? (
            <IoCheckmarkCircle className="exported-icon" />
          ) : (
            <IoCloseCircle className="not-exported-icon" />
          )}
        </div>
      );
    },
    [bankFileService]
  );

  // Stable actions column renderer function
  const renderActionsColumn = useCallback(
    (rowData) => <MemoizedActionsColumn rowData={rowData} onLinkChild={onLinkChild} onSplitTransaction={onSplitTransaction} transactionsService={transactionsService} />,
    [onLinkChild, onSplitTransaction, transactionsService]
  );


  const columns = useMemo(() => {
    const baseColumns = [
      <Column key="date" width={100} sortable resizable>
        <HeaderCell>Date</HeaderCell>
        <Cell dataKey="date" />
      </Column>,
      <Column key="amount" width={100} sortable resizable>
        <HeaderCell>Amount</HeaderCell>
        <Cell dataKey="amount">{formatAmount}</Cell>
      </Column>,
      <Column key="particulars" width={120} sortable resizable>
        <HeaderCell>Particulars</HeaderCell>
        <Cell dataKey="particulars" />
      </Column>,
      <Column key="code" width={120} sortable resizable>
        <HeaderCell>Code</HeaderCell>
        <Cell dataKey="code" />
      </Column>,
      <Column key="reference" width={120} sortable resizable>
        <HeaderCell>Reference</HeaderCell>
        <Cell dataKey="reference" />
      </Column>,
      <Column key="otherParty" flexGrow={2} width={228} sortable resizable>
        <HeaderCell>Other Party</HeaderCell>
        <Cell dataKey="otherParty" />
      </Column>,
      <Column key="otherPartyAccount" width={180} sortable resizable>
        <HeaderCell>Other Party Account</HeaderCell>
        <Cell dataKey="otherPartyAccount" />
      </Column>,
      <Column key="type" width={60} sortable>
        <HeaderCell>Type</HeaderCell>
        <Cell dataKey="type" />
      </Column>,
      <Column key="ExportType" width={100}>
        <HeaderCell>Export Type</HeaderCell>
        <Cell dataKey="exportType">{exportAs}</Cell>
      </Column>,
      <Column key="exported" width={100} sortable>
        <HeaderCell>Exported</HeaderCell>
        <Cell dataKey="ignore">{renderExportStatus}</Cell>
      </Column>,
    ];

    if (isAdmin) {
      return [
        <Column key="actions" width={135} sortable>
          <HeaderCell>Actions</HeaderCell>
          <Cell dataKey="childId">{renderActionsColumn}</Cell>
        </Column>,
        ...baseColumns,
      ];
    }

    return baseColumns;
  }, [isAdmin, formatAmount, renderExportStatus, renderActionsColumn]);

  // Stable row className function
  const getRowClassName = useCallback((rowData) => {
    const baseClass = "expanded-row-content";
    return rowData?.ignore ? `${baseClass} ignored-transaction` : baseClass;
  }, []);

  return (
    <Table
      rowKey="id"
      data={data}
      height={320}
      bordered
      cellBordered
      style={{ width: "100%" }}
      rowClassName={getRowClassName}
      className="bank-transactions-table"
      sortColumn={sortColumn}
      sortType={sortType}
      onSortColumn={onSortColumn}
    >
      {columns}
    </Table>
  );
}

export default memo(BankTransactionsTable);
